import { useEffect, useMemo } from "react";

import { FieldModule } from "../../DynamicSection/components/FieldModule";
import useClientDemographicsForm from "../hooks/useClientDemographicsForm";
import { getSpanClassForClientDemographics } from "../utils/layout";

const isValueFilled = (value) => {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === "string") {
    return value.trim().length > 0;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === "object") {
    return Object.keys(value).length > 0;
  }
  return Boolean(value);
};

const ClientDemographicsForm = ({
  schema,
  data,
  label,
  questionKey,
  handleAnswerChange,
  placeholder,
  subTitle,
  footer,
  readOnly,
  setEnableSave,
}) => {
  const { form, orderedFields, getPropDefinition, requiredFields, isFlatSchema, onChange } =
    useClientDemographicsForm({ schema, data, questionKey, handleAnswerChange });

  const addressFieldName = useMemo(
    () => orderedFields.find((field) => field === "address" || field.endsWith("_address")) ?? null,
    [orderedFields]
  );
  const locationFieldName = useMemo(
    () =>
      orderedFields.find((field) => field === "incident_location" || field.endsWith("_location")) ??
      null,
    [orderedFields]
  );
  const cityFieldName = useMemo(
    () => orderedFields.find((field) => field === "city" || field.endsWith("_city")),
    [orderedFields]
  );

  const addressValue = addressFieldName ? form?.[addressFieldName] : undefined;
  const locationValue = locationFieldName ? form?.[locationFieldName] : undefined;
  const cityValue = cityFieldName ? form?.[cityFieldName] : undefined;
  const addressFilled = isValueFilled(addressValue);
  const locationFilled = isValueFilled(locationValue);
  const cityFilled = isValueFilled(cityValue);

  const addressOrLocationPresent = Boolean(addressFieldName || locationFieldName);
  const requireAddress = Boolean(addressFieldName) && !addressFilled && !locationFilled;
  const requireLocation = Boolean(locationFieldName) && !addressFilled && !locationFilled;

  const effectiveRequiredFields = useMemo(
    () =>
      requiredFields.filter(
        (field) =>
          field !== addressFieldName && field !== locationFieldName && field !== cityFieldName
      ),
    [requiredFields, addressFieldName, locationFieldName, cityFieldName]
  );
  const baseRequiredFilled = useMemo(
    () => effectiveRequiredFields.every((field) => isValueFilled(form?.[field])),
    [effectiveRequiredFields, form]
  );

  useEffect(() => {
    if (typeof setEnableSave !== "function") {
      return;
    }
    const requiresAddressOrLocation = addressOrLocationPresent
      ? addressFilled || locationFilled
      : true;
    const canSave = baseRequiredFilled && requiresAddressOrLocation;
    setEnableSave(canSave);
  }, [setEnableSave, baseRequiredFilled, addressOrLocationPresent, addressFilled, locationFilled]);

  return (
    <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-12">
      {orderedFields.map((name, idx) => {
        const prop = getPropDefinition(name);
        const isSingleQuestion = isFlatSchema || orderedFields.length === 1;
        const labelToRender = isSingleQuestion ? label : prop?.title || "";
        const fallbackSpan = isSingleQuestion
          ? "md:col-span-12"
          : idx % 2 === 0
            ? "md:col-span-5"
            : "md:col-span-7";
        const spanClass = getSpanClassForClientDemographics(name, fallbackSpan);

        const isAddressField = name === addressFieldName;
        const isLocationField = Boolean(locationFieldName) && name === locationFieldName;
        const isCityField = Boolean(cityFieldName) && name === cityFieldName;
        const dynamicRequired =
          (isAddressField && requireAddress) || (isLocationField && requireLocation);
        const isRequired =
          (requiredFields.includes(name) && name !== addressFieldName) || dynamicRequired;

        const shouldDisableCityField = isCityField && Boolean(locationFieldName) && !locationFilled;

        const fieldProps = {};
        if (shouldDisableCityField) {
          fieldProps.disabled = true;
        }
        if (isLocationField) {
          fieldProps.error = requireLocation && !locationFilled;
        }
        if (isCityField) {
          fieldProps.error = false;
        }
        const mergedFieldProps = Object.keys(fieldProps).length > 0 ? fieldProps : undefined;

        return (
          <div key={name} className={`${spanClass} min-w-0`}>
            <FieldModule
              label={labelToRender}
              name={name}
              prop={prop}
              value={form?.[name]}
              formValues={form}
              onChange={onChange}
              enumUrl={typeof prop?.enum === "string" ? prop.enum : undefined}
              placeholder={placeholder}
              subTitle={subTitle}
              footer={footer}
              readOnly={readOnly}
              isRequired={isRequired}
              props={mergedFieldProps}
            />
          </div>
        );
      })}
    </div>
  );
};

export default ClientDemographicsForm;
